import React, { useState, useMemo, useCallback } from 'react'
import { t } from 'ttag'
import {
  DivisionDto,
  DivisionId,
  GoalId,
  GroupView,
  Lang,
  DivisionCategoryRow,
  GoalWithProgress,
  DivisionCategoryType,
} from '../../services/api.types'
import YumButton from '../YumButton/YumButton'
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer'
import { ReactComponent as FilterIconNew } from '../../assets/v3/icons/ranking/filter.svg'
import { ReactComponent as FilterActiveIcon } from '../../assets/v2/icons/icon-filter-active.svg'
import { ReactComponent as CountriesSeparatedIcon } from '../../assets/v2/icons/icon-countries-separated.svg'
import { ReactComponent as CountriesMergedIcon } from '../../assets/v2/icons/icon-countries-merged.svg'
import { ReactComponent as OnlyMyCountryIcon } from '../../assets/v2/icons/icon-only-my-country.svg'
import { ReactComponent as OnlyMyGroupIcon } from '../../assets/v2/icons/icon-only-my-group.svg'
import { ReactComponent as CollapseClosedIcon } from '../../assets/v2/icons/icon-collapse-closed.svg'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Chip from '@material-ui/core/Chip'
import { localizeCountries } from '../YumCountrySelect/YumCountrySelect'
import { ActivityFilter, ActivityView } from '../../views/Activity/store/ActivityReducer'
import { List, ListItem, ListItemIcon } from '@material-ui/core'
import { ReactComponent as CheckedCircleIcon } from '../../assets/v2/icons/checked-circle-blue.svg'
import { ReactComponent as EmptyCircleIcon } from '../../assets/v2/icons/empty-circle-blue.svg'
import ListItemText from '@material-ui/core/ListItemText'
import Collapse from '@material-ui/core/Collapse'
import { Capacitor } from '@capacitor/core'
import { currentLang, isRtl } from '../../i18nInit'
import YumButtonGroup from '../YumButtonGroup/YumButtonGroup'
import { getDivisionCategoryTypeTranslation } from '../../views/TeamManagement/TeamManagement'

export interface IYumActivityFilterProps {
  defaultActivityFilter: ActivityFilter
  currentActivityFilter: ActivityFilter
  countries: string[]
  divisions: DivisionDto[]
  divisionCategories: DivisionCategoryRow[]
  //companyGroups: { [categoryId: string]: DivisionDto[] }
  activityView: ActivityView
  groupView: GroupView
  country: string
  divisionId: DivisionId
  isLegacyRanking: boolean
  goalId: GoalId
  goalDto: GoalWithProgress
  token?: string
  compactLayout?: boolean
  hideTeamFilter?: boolean
  hideCountryFilter?: boolean
  hiddenCustomGroupCategoryIds?: string[]
}

export interface IYumActivityFilterDispatchProps {
  getCurrentGoalActivityList(payload?: {
    goalId: GoalId
    divisionId: DivisionId
    activityFilter?: ActivityFilter
    activityView?: ActivityView
    groupView?: GroupView
    groupSum?: boolean
    token?: string
  }): void
  getCurrentGoalActivityListClear(payload: { goalId: GoalId; updateGroupView?: boolean }): void
  setActivityFilter(payload: { goalId: GoalId; activityFilter: ActivityFilter }): void
  setGroupView(payload?: any): void
  getDivisions(payload: { companyId: string; categoryId: string }): void
}

const YumActivityFilter: React.FC<IYumActivityFilterProps & IYumActivityFilterDispatchProps> = (props) => {
  const {
    defaultActivityFilter,
    currentActivityFilter,
    countries,
    divisions,
    divisionCategories,
    //companyGroups,
    activityView,
    groupView,
    country,
    divisionId,
    isLegacyRanking,
    goalId,
    goalDto,
    token,
    compactLayout,
    hideTeamFilter,
    hideCountryFilter,
    hiddenCustomGroupCategoryIds = [],
  } = props
  const [teams, setTeams] = useState([] as DivisionDto[])
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [activityFilter, setActivityFilterState] = useState(currentActivityFilter || defaultActivityFilter)
  const [groupViewState, setGroupViewState] = useState(groupView)
  const [groupCollapsed, setGroupCollapsed] = useState<{ [groupId: string]: boolean }>({})
  const [countryCollapsed, setCountryCollapsed] = useState(!!(divisions && divisions.length))
  const [divisionCategoryCollapsed, setDivisionCategoryCollapsed] = useState<{ [categoryId: string]: boolean }>({})
  // const loadedCategoriesRef = React.useRef<Set<string>>(new Set())

  const isIndividualLeaderboard = [ActivityView.INDIVIDUAL, ActivityView.COLLECTIVE].includes(activityView)

  React.useEffect(() => {
    setTeams(divisions?.filter((d) => d.companyId === goalDto?.companyId && d.categoryId === null) || [])
  }, [divisions, goalDto?.companyId])

  // // Load groups for all DivisionCategories when in individual view
  // React.useEffect(() => {
  //   if (isIndividualLeaderboard && goalDto?.companyId && divisionCategories) {
  //     divisionCategories.forEach((category) => {
  //       // Check if we already have groups for this category or if we've already loaded it
  //       if (!loadedCategoriesRef.current.has(category.id)) {
  //         const groups = divisions.filter((division) => division.categoryId === category.id)
  //         if (!groups || groups.length === 0) {
  //           loadedCategoriesRef.current.add(category.id)
  //           props.getDivisions({
  //             companyId: goalDto.companyId,
  //             categoryId: category.id,
  //           })
  //         }
  //       }
  //     })
  //   }
  // }, [activityView, goalDto?.companyId, divisionCategories, divisions, props])

  const filterActive = useMemo(
    () => JSON.stringify(defaultActivityFilter) !== JSON.stringify(activityFilter),
    [defaultActivityFilter, activityFilter],
  )
  const groupFilterActive = useMemo(() => groupView && groupView !== GroupView.COUNTRIES_MERGED, [groupView])

  const setActivityFilter = (activityFilter: ActivityFilter) => {
    if (isLegacyRanking) {
      props.getCurrentGoalActivityListClear({ goalId })
      props.getCurrentGoalActivityList({ goalId, divisionId, activityFilter, activityView, token })
    } else {
      props.setActivityFilter({ goalId, activityFilter })
    }
  }

  const setGroupView = (groupView: GroupView) => {
    if (isLegacyRanking) {
      props.getCurrentGoalActivityListClear({ goalId, updateGroupView: true })
      props.getCurrentGoalActivityList({ goalId, divisionId, groupView, activityView, token })
    } else {
      props.setGroupView({ goalId, groupView })
    }
  }

  const reset = useCallback(() => {
    if (isIndividualLeaderboard) {
      setActivityFilter(defaultActivityFilter)
      setActivityFilterState(defaultActivityFilter)
      setIsDrawerOpen(false)
    } else {
      setGroupView(GroupView.COUNTRIES_MERGED)
      setGroupViewState(GroupView.COUNTRIES_MERGED)
      setIsDrawerOpen(false)
    }
  }, [activityView, defaultActivityFilter, setActivityFilter, setGroupView])

  const filter = useCallback(() => {
    if (isIndividualLeaderboard) {
      setActivityFilter(activityFilter)
    } else {
      setGroupView(groupViewState)
    }
    setIsDrawerOpen(false)
  }, [activityView, activityFilter, groupViewState, setActivityFilter, setGroupView])

  const getDivisionCategoryId = useCallback(
    (divisionId: DivisionId): string | null => {
      // Use categoryId to keep selections exclusive within each filter group.
      const division = divisions.find((item) => item.id === divisionId)
      return division ? (division.categoryId ?? null) : null
    },
    [divisions],
  )

  const removeDivisionIdsFromCategory = useCallback(
    (divisionIds: DivisionId[], categoryId: string | null) =>
      divisionIds.filter((id) => getDivisionCategoryId(id) !== categoryId),
    [getDivisionCategoryId],
  )

  const toggleDivision = useCallback(
    (divisionId: DivisionId) => {
      setActivityFilterState((prevState) => {
        // Only one team can be selected at a time in the Team filter group.
        const isSelected = prevState.divisionIds.includes(divisionId)
        if (isSelected) {
          return { ...prevState, divisionIds: prevState.divisionIds.filter((id) => id !== divisionId) }
        }
        const divisionIds = removeDivisionIdsFromCategory(prevState.divisionIds, null)
        return { ...prevState, divisionIds: [...divisionIds, divisionId] }
      })
    },
    [removeDivisionIdsFromCategory],
  )

  const toggleCountry = useCallback((country: string) => {
    setActivityFilterState((prevState) => {
      // Only one country can be selected at a time in the Country filter group.
      const isSelected = prevState.countries.includes(country)
      const countries = isSelected ? prevState.countries.filter((c) => c !== country) : [country]
      return { ...prevState, countries }
    })
  }, [])

  const toggleDivisionCategoryGroup = useCallback(
    (groupId: DivisionId) => {
      setActivityFilterState((prevState) => {
        // Only one group can be selected per division category group.
        const isSelected = prevState.divisionIds.includes(groupId)
        if (isSelected) {
          return { ...prevState, divisionIds: prevState.divisionIds.filter((id) => id !== groupId) }
        }
        const categoryId = getDivisionCategoryId(groupId)
        const divisionIds = removeDivisionIdsFromCategory(prevState.divisionIds, categoryId)
        return { ...prevState, divisionIds: [...divisionIds, groupId] }
      })
    },
    [getDivisionCategoryId, removeDivisionIdsFromCategory],
  )

  const handleGroupViewClick = useCallback((groupView: GroupView) => {
    setGroupViewState(groupView)
  }, [])

  const toggleDrawer = useCallback(
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event &&
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return
      }
      setIsDrawerOpen(open)
    },
    [],
  )

  const toggleGroupCollapse = useCallback((groupId?: string) => {
    if (groupId) {
      setGroupCollapsed((prev) => ({
        ...prev,
        [groupId]: !prev[groupId],
      }))
    } else {
      // For backward compatibility, toggle all groups
      setGroupCollapsed((prev) => {
        const newState = { ...prev }
        Object.keys(newState).forEach((key) => {
          newState[key] = !newState[key]
        })
        return newState
      })
    }
  }, [])

  const toggleCountryCollapse = useCallback(() => {
    setCountryCollapsed((prev) => !prev)
  }, [])

  const toggleDivisionCategoryCollapse = useCallback((categoryId: string) => {
    setDivisionCategoryCollapsed((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }))
  }, [])

  const iOS = Capacitor.getPlatform() === 'ios'

  const groupViewOptions = useMemo(() => {
    const options = [
      { key: GroupView.COUNTRIES_MERGED, label: t`Countries merged` },
      { key: GroupView.COUNTRIES_SEPARATED, label: t`Countries separated` },
    ]
    if (country) {
      options.push({
        key: GroupView.ONLY_MY_COUNTRY,
        label: currentLang === Lang.EN ? t`Only my country (by teams)` : t`Only my country (by groups)`,
      })
    }
    if (divisionId) {
      options.push({
        key: GroupView.ONLY_MY_GROUP,
        label: currentLang === Lang.EN ? t`Only my team (by countries)` : t`Only my group (by countries)`,
      })
    }
    return options
  }, [country, divisionId])

  return (countries && countries.length) ||
    (divisions && divisions.length) ||
    (divisionCategories && divisionCategories.length) ? (
    <>
      <YumButton
        variant="outlined"
        size="small"
        aria-label={t`Filter`}
        //style={isRtl || compactLayout ? { marginRight: '8px' } : { marginLeft: '8px' }}
        style={
          compactLayout
            ? isRtl
              ? { marginLeft: '8px' }
              : { marginRight: '8px' }
            : !isRtl
              ? { marginLeft: '8px' }
              : { marginRight: '8px' }
        }
        onClick={toggleDrawer(true)}
      >
        <FilterIconNew style={{ width: '1rem', height: '1rem' }} />
        <Box
          display={{ xs: 'none', sm: 'inline' }}
          style={isRtl ? { marginRight: '8px' } : { marginLeft: '8px' }}
        >{t`Filter`}</Box>
        {(isIndividualLeaderboard ? filterActive : groupFilterActive) ? (
          <FilterActiveIcon
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
            }}
          />
        ) : (
          ''
        )}
      </YumButton>
      <SwipeableDrawer
        anchor="bottom"
        open={isDrawerOpen}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        disableBackdropTransition={!iOS}
        disableDiscovery={iOS}
        PaperProps={{
          style: { display: 'flex', flexDirection: 'column', overflowY: 'hidden' },
        }}
      >
        <div style={{ flexGrow: 1, padding: '8px 10px 0 10px', overflowY: 'auto' }}>
          {isIndividualLeaderboard ? (
            <>
              {teams && teams.length && !hideTeamFilter ? (
                <section style={{ marginBottom: 20 }} aria-label={t`Team`}>
                  <Collapse
                    in={!groupCollapsed['teams']}
                    collapsedSize={50}
                    style={{
                      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
                      borderRadius: 20,
                    }}
                  >
                    <div
                      onClick={() => toggleGroupCollapse('teams')}
                      style={{ height: 50, padding: 15, cursor: 'pointer' }}
                    >
                      <CollapseClosedIcon
                        style={{
                          float: isRtl ? 'left' : 'right',
                          transition: '.4s all',
                          transform: groupCollapsed['teams'] ? '' : 'rotate(-180deg)',
                        }}
                      />
                      <Typography variant="h2" style={{ lineHeight: '20px' }}>{t`Team`}</Typography>
                    </div>
                    <div style={{ padding: '5px 15px 10px 15px' }}>
                      {teams.map(({ id, name }, index) => (
                        <Chip
                          key={index}
                          label={name}
                          onClick={() => toggleDivision(id)}
                          variant={activityFilter.divisionIds.includes(id) ? 'outlined' : 'default'}
                        />
                      ))}
                    </div>
                  </Collapse>
                </section>
              ) : (
                ''
              )}

              {divisionCategories && divisionCategories.length && isIndividualLeaderboard
                ? divisionCategories
                    .filter((category) => {
                      if (hiddenCustomGroupCategoryIds.includes(category.id)) return false
                      const groups = divisions.filter((division) => division.categoryId === category.id) || []
                      return groups.length > 0 // Only show categories that have groups
                    })
                    .map((category) => {
                      const categoryGroups = divisions.filter((division) => division.categoryId === category.id) || []
                      // Prefer the custom group name when the category type is CUSTOM.
                      const categoryTitle =
                        category.type === DivisionCategoryType.CUSTOM && category.customName
                          ? category.customName
                          : getDivisionCategoryTypeTranslation(category.type)
                      return (
                        <section key={category.id} style={{ marginBottom: '20px' }} aria-label={categoryTitle}>
                          <Collapse
                            in={!divisionCategoryCollapsed[category.id]}
                            collapsedSize={50}
                            style={{
                              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
                              borderRadius: 20,
                            }}
                          >
                            <div
                              onClick={() => toggleDivisionCategoryCollapse(category.id)}
                              style={{ height: 50, padding: 15, cursor: 'pointer' }}
                            >
                              <CollapseClosedIcon
                                style={{
                                  float: isRtl ? 'left' : 'right',
                                  transition: '.4s all',
                                  transform: divisionCategoryCollapsed[category.id] ? '' : 'rotate(-180deg)',
                                }}
                              />
                              <Typography variant="h2" style={{ lineHeight: '20px' }}>
                                {categoryTitle}
                              </Typography>
                            </div>
                            <div style={{ padding: '5px 15px 10px 15px' }}>
                              {categoryGroups.map((group, index) => (
                                <Chip
                                  key={index}
                                  label={group.name}
                                  onClick={() => toggleDivisionCategoryGroup(group.id)}
                                  variant={activityFilter.divisionIds.includes(group.id) ? 'outlined' : 'default'}
                                />
                              ))}
                            </div>
                          </Collapse>
                        </section>
                      )
                    })
                : ''}

              {countries && countries.length && !hideCountryFilter ? (
                <section style={{ marginBottom: '20px' }} aria-label={t`Country`}>
                  <Collapse
                    in={!countryCollapsed}
                    collapsedSize={50}
                    style={{
                      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
                      borderRadius: 20,
                    }}
                  >
                    <div onClick={toggleCountryCollapse} style={{ height: 50, padding: 15, cursor: 'pointer' }}>
                      <CollapseClosedIcon
                        style={{
                          float: isRtl ? 'left' : 'right',
                          transition: '.4s all',
                          transform: countryCollapsed ? '' : 'rotate(-180deg)',
                        }}
                      />
                      <Typography variant="h2" style={{ lineHeight: '20px' }}>{t`Country`}</Typography>
                    </div>
                    <div style={{ padding: '5px 15px 10px 15px' }}>
                      {localizeCountries(countries).map(({ code, countryWithFlag }, index) => (
                        <Chip
                          key={index}
                          label={countryWithFlag}
                          onClick={() => toggleCountry(code)}
                          variant={activityFilter.countries.includes(code) ? 'outlined' : 'default'}
                        />
                      ))}
                    </div>
                  </Collapse>
                </section>
              ) : (
                ''
              )}
            </>
          ) : (
            <section aria-label={t`View options`}>
              <Typography variant="h2">{t`View options`}</Typography>
              <List>
                {groupViewOptions.map(({ key, label }, index) => (
                  <ListItem
                    key={key}
                    role={undefined}
                    button
                    disableRipple
                    onClick={() => handleGroupViewClick(key)}
                    selected={groupViewState === key}
                  >
                    <ListItemIcon>{groupViewState === key ? <CheckedCircleIcon /> : <EmptyCircleIcon />}</ListItemIcon>
                    <ListItemText id={key} primary={label} />
                    <ListItemIcon style={{ minWidth: 'auto' }}>
                      <>
                        {key === GroupView.COUNTRIES_SEPARATED ? <CountriesSeparatedIcon /> : ''}
                        {key === GroupView.COUNTRIES_MERGED ? <CountriesMergedIcon /> : ''}
                        {key === GroupView.ONLY_MY_COUNTRY ? <OnlyMyCountryIcon /> : ''}
                        {key === GroupView.ONLY_MY_GROUP ? <OnlyMyGroupIcon /> : ''}
                      </>
                    </ListItemIcon>
                  </ListItem>
                ))}
              </List>
            </section>
          )}
        </div>

        <div
          style={{
            flexShrink: 0,
            padding: '20px 10px',
          }}
        >
          <YumButtonGroup>
            <YumButton variant="outlined" onClick={reset}>{t`Reset`}</YumButton>
            <YumButton onClick={filter}>{t`Filter`}</YumButton>
          </YumButtonGroup>
        </div>
      </SwipeableDrawer>
    </>
  ) : null
}

export default YumActivityFilter
